import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import type { ColumnsType } from 'antd/es/table';
import {
    Alert as ServaAlert,
    Button,
    Checkbox,
    Col,
    Input,
    Row,
    Skeleton,
    Space,
    Spin,
    Table,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import {
    buildLNKErrorMessage,
    filterLinkedAdminRows,
    updateLinkedAdminRow,
} from '../Shared/LNKHelper';
import { invalidateReportMenu } from '../Shared/ReportMenuQuery';
import Administration from '../../Constants/Endpoints/Administration';
import AddDelUserAdminTenantRequest from '../../Interfaces/Requests/Administration/AddDelUserAdminTenantRequest';
import GetUserAdminTenantModelsUsingUserIDRequest from '../../Interfaces/Requests/Administration/GetUserAdminTenantModelsUsingUserIDRequest';
import GetUserAdminTenantModelsUsingUserIDResponse from '../../Interfaces/Responses/Administration/GetUserAdminTenantModelsUsingUserIDResponse';

type UserTenantProps = {
    userId: number;
    userName: string;
    close: () => void;
};

const UserTenants: React.FC<UserTenantProps> = ({ ...props }) => {
    const queryClient = useQueryClient();

    const [tenants, setTenants] = useState<GetUserAdminTenantModelsUsingUserIDResponse[]>([]);
    const [hideUnlinkedTenants, setHideUnlinkedTenants] = useState(false);
    const [searchText, setSearchText] = useState('');

    const [currentError, setCurrentError] = useState('');

    const { isLoading: isGetUserTenantsLoading, isError: isGetUserTenantsError, error: getUserTenantsError, refetch: refetchUserTenants } = useQuery(
        ['user-admin-tenants', props.userId],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_USER_ADMIN_TENANT_MODELS_USING_USER_ID}`;
                const requestData: GetUserAdminTenantModelsUsingUserIDRequest = {
                    gut_userid: props.userId,
                };
                return await AxiosUtil.get<GetUserAdminTenantModelsUsingUserIDResponse[], GetUserAdminTenantModelsUsingUserIDRequest>(url, requestData);
            } catch (err) {
                setCurrentError('GET_USER_ADMIN_TENANT_MODELS_USING_USER_ID: [Get User Tenants] Internal Error: ' + err);
                console.error('GET_USER_ADMIN_TENANT_MODELS_USING_USER_ID [Get User Tenants] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            enabled: props.userId > 0,
            onError: (err: unknown) => {
                setCurrentError('GET_USER_ADMIN_TENANT_MODELS_USING_USER_ID: [Get User Tenants] Internal Error: ' + err);
                console.error('GET_USER_ADMIN_TENANT_MODELS_USING_USER_ID: [Get User Tenants] Internal Error: ', err);
            },
            onSuccess: (response: GetUserAdminTenantModelsUsingUserIDResponse[]) => {
                if (response) {
                    setTenants(response);
                }
            },
        }
    );

    const { mutate: addRemoveTenant, isLoading: isAddRemoveTenantLoading, isError: isAddRemoveTenantError, error: getAddRemoveTenantError } = useMutation(
        async ({ add, tenant, }: {
            add: boolean;
            tenant: GetUserAdminTenantModelsUsingUserIDResponse;
        }) => {
            let responseData = 0;
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.ADD_DEL_USER_ADMIN_TENANTS}`;
                const addDelUserAdminTenant: AddDelUserAdminTenantRequest = {
                    id: tenant.id,
                    tenantName: tenant.tenantName,
                    connString: tenant.connString,
                    userId: props.userId,
                    userName: props.userName,
                    isAdd: add,
                    isDel: !add,
                };
                const requestData: AddDelUserAdminTenantRequest[] = [ addDelUserAdminTenant, ];
                responseData = await AxiosUtil.post<number, AddDelUserAdminTenantRequest[]>(url, requestData);
            } catch (err) {
                setCurrentError('ADD_DEL_USER_ADMIN_TENANTS: [Add/Remove User Tenants] Internal Error: ' + err);
                console.error('ADD_DEL_USER_ADMIN_TENANTS [Add/Remove User Tenants] Internal Error: ', err);
                return Promise.reject(err);
            }
            return { response: responseData, add, tenant };
        },
        {
            onError: (err: unknown) => {
                setCurrentError('ADD_DEL_USER_ADMIN_TENANTS: [Add/Remove User Tenants] Internal Error: ' + err);
                console.error('ADD_DEL_USER_ADMIN_TENANTS: [Add/Remove User Tenants] Internal Error: ', err);
            },
            onSuccess: ({ response, add, tenant }) => {
                if (response) {
                    setTenants(updateLinkedAdminRow(tenants, tenant.id, add));
                    void refetchUserTenants();
                    invalidateReportMenu(queryClient);
                }
            },
        }
    );

    const handleCheckboxChange = (checked: boolean, tenant: GetUserAdminTenantModelsUsingUserIDResponse) => {
        const params = { add: checked, tenant: tenant, };
        addRemoveTenant(params);
    };

    const columns: ColumnsType<GetUserAdminTenantModelsUsingUserIDResponse> = [
        {
            title: 'Linked',
            key: 'isLinked',
            render: (text: unknown, record: GetUserAdminTenantModelsUsingUserIDResponse) => (
                <>
                    <Checkbox
                        checked={record.isLinked}
                        onChange={(e) => handleCheckboxChange(e.target.checked, record)}
                    ></Checkbox>
                </>
            ),
        },
        { title: 'Tenant Name', dataIndex: 'tenantName', key: 'tenantName' },
    ];

    const visibleTenants = filterLinkedAdminRows(
        tenants,
        hideUnlinkedTenants,
        searchText.trim(),
        (tenant, normalizedSearchText) => normalizedSearchText === '' || tenant.tenantName.toLowerCase().includes(normalizedSearchText)
    );

    const handleShowLinkedTenants = () => {
        setHideUnlinkedTenants(!hideUnlinkedTenants);
    };

    const handleSearchTextChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(aValue.target.value);
    };

    const doSearch = (sValue: string) => {
        setSearchText(sValue);
    };

    const determineWhichError = () => {
        return buildLNKErrorMessage(
            currentError,
            isGetUserTenantsError,
            getUserTenantsError,
            isAddRemoveTenantError,
            getAddRemoveTenantError
        );
    };

    return (
        <>
            <Spin spinning={isGetUserTenantsLoading || isAddRemoveTenantLoading}>
                <div style={{ display: 'table', margin: 'auto', textAlign: 'center', width: '50%' }}>
                {
                    (
                        isGetUserTenantsError ||
                        isAddRemoveTenantError
                    )
                    &&
                    (
                        <ServaAlert
                            className={'mb-2'}
                            message={determineWhichError()}
                            showIcon={true}
                            type='error'
                        ></ServaAlert>
                    )
                }
                </div>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '8px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage User Links To Tenants</Title>
                    </Text>
                </Space>
                <div style={{ width: '100%', overflowX: 'auto' }}>
                    <Row gutter={16}>
                        <Col span={4}>
                            <Button
                                onClick={handleShowLinkedTenants}
                                size='small'
                                style={{
                                    background: 'none',
                                    backgroundColor: 'lightgray',
                                    color: 'blue',
                                    cursor: 'pointer',
                                }}
                                type='text'
                            >
                                {hideUnlinkedTenants ? 'Show All Tenants' : 'Hide Unlinked Tenants'}
                            </Button>
                        </Col>
                        <Col span={4}></Col>
                        <Col span={8}>
                            <Input
                                allowClear={true}
                                value={searchText}
                                search={true}
                                style={{alignContent: 'center', width: '75%'}}
                                onChange={handleSearchTextChange}
                                onSearch={doSearch}
                            ></Input>
                        </Col>
                        <Col span={4}></Col>
                        <Col span={4}><Text type='secondary' italic ellipsis>{props.userName}</Text></Col>
                    </Row>
                    <div style={{ padding: '4px' }} />
                    {isGetUserTenantsLoading && tenants.length === 0 ? (
                        <Skeleton />
                    ) : (
                        <Table rowKey='id' columns={columns} dataSource={visibleTenants} />
                    )}
                </div>
            </Spin>
        </>
    );
};

export default UserTenants;
